
import { Vue, Options, prop } from "vue-class-component";
import moment from "moment";
import {
  Cell,
  DateRange,
  HoverDate,
  Panel,
  PanelDate,
  PanelSelectingStart,
  Range,
  Side
} from "./type";
import { cloneDeep } from "lodash";

class Props {
  modelValue = prop<DateRange>({
    type: Object,
    default: () => new DateRange()
  });
  defaultDays = prop({
    type: Number,
    default: 14,
    validator: (v: number) => [0, 7, 14, 30, 90].includes(v)
  });
  autoApply = prop({ type: Boolean, default: true });
  max = prop<Date | null>({
    type: Date,
    default: null
  });
}

@Options({
  name: "DateRangeCompare",
  emits: ["update:modelValue", "select-date", "preset", "reset", "apply"]
})
export default class DateRangeCompare extends Vue.with(Props) {
  /* ---------------------- State ---------------------- */
  get weekdays() {
    return [
      this.$t("dashboard.dateRangeCompare.weekdays.mon"),
      this.$t("dashboard.dateRangeCompare.weekdays.tue"),
      this.$t("dashboard.dateRangeCompare.weekdays.wed"),
      this.$t("dashboard.dateRangeCompare.weekdays.thu"),
      this.$t("dashboard.dateRangeCompare.weekdays.fri"),
      this.$t("dashboard.dateRangeCompare.weekdays.sat"),
      this.$t("dashboard.dateRangeCompare.weekdays.sun")
    ];
  }
  today = moment().startOf("day");

  get presets() {
    return [
      { label: this.$t("dashboard.dateRangeCompare.presets.customDate"), days: 0 },
      { label: this.$t("dashboard.dateRangeCompare.presets.last7Days"), days: 7 },
      { label: this.$t("dashboard.dateRangeCompare.presets.last14Days"), days: 14 },
      { label: this.$t("dashboard.dateRangeCompare.presets.lastMonth"), days: 30 },
      { label: this.$t("dashboard.dateRangeCompare.presets.last3Months"), days: 90 }
    ];
  }

  selectedPreset = 0;
  localDate = new DateRange();
  hoverMoment = new HoverDate({ main: null, compare: null });
  selecting = new PanelSelectingStart();
  panel = new Panel();

  /* ---------------------- Refs ---------------------- */
  openOverlayPanel(event: Event) {
    (this.$refs as any)?.opDateRangeCompare?.toggle(event);
  }
  closeOverlayPanel(event?: Event) {
    (this.$refs as any)?.opDateRangeCompare?.hide(event);
  }

  /* ---------------------- Helpers ---------------------- */
  private mDay(cell: Cell) {
    return moment([cell.year, cell.month, cell.day]).startOf("day");
  }
  private toStart(d?: Date | moment.Moment | null) {
    return (d ? moment(d) : moment()).startOf("day");
  }
  private toLocalDate(m: moment.Moment) {
    return moment
      .utc([m.year(), m.month(), m.date()])
      .startOf("day")
      .toDate();
  }

  private normRange(side: Side) {
    const range = this.localDate[side],
      hover = this.hoverMoment[side];
    if (!range?.start) return null;
    const s = this.toStart(range.start);
    const e = range.end
      ? this.toStart(range.end)
      : hover
      ? this.toStart(hover)
      : s;
    return { s, e, min: s.isBefore(e) ? s : e, max: s.isBefore(e) ? e : s };
  }

  computePanelFromDate(m: moment.Moment): PanelDate {
    const month = m.month(),
      year = m.year(),
      daysInMonth = m.daysInMonth();
    const firstDay = (moment([year, month, 1]).day() + 6) % 7;
    const total = Math.ceil((firstDay + daysInMonth) / 7) * 7;
    return {
      month,
      year,
      daysInMonth,
      firstDay,
      trailing: total - (firstDay + daysInMonth)
    };
  }

  syncPanelToRange() {
    if (this.localDate.main.start)
      this.panel.main = this.computePanelFromDate(
        moment(this.localDate.main.start)
      );
    if (this.localDate.compare.start)
      this.panel.compare = this.computePanelFromDate(
        moment(this.localDate.compare.start)
      );
  }

  prevMonth(side: Side) {
    const p = this.panel[side];
    this.panel[side] = this.computePanelFromDate(
      moment([p.year, p.month, 1]).subtract(1, "month")
    );
  }

  nextMonth(side: Side) {
    const p = this.panel[side];
    this.panel[side] = this.computePanelFromDate(
      moment([p.year, p.month, 1]).add(1, "month")
    );
  }

  monthYearLabel(p: PanelDate) {
    return moment([p.year, p.month, 1]).format("MMMM YYYY");
  }
  formatDate(d: Date | null, fmt = "D MMM YYYY") {
    return d ? moment(d).format(fmt) : "";
  }
  formatRange(s: Date | null, e: Date | null) {
    if (!s || !e) return "";
    const ms = moment(s),
      me = moment(e);
    return ms.month() === me.month() && ms.year() === me.year()
      ? `${ms.format("D")} - ${me.format("D MMM YYYY")}`
      : `${ms.format("D MMM YYYY")} - ${me.format("D MMM YYYY")}`;
  }

  getCalendarGrid(p: PanelDate): Cell[] {
    const year = p.year;
    const month = p.month;
    const firstDay = p.firstDay;
    const daysInMonth = p.daysInMonth;

    // prev month cells
    const prevMonth = moment([year, month, 1]).subtract(1, "month");
    const prevDays = prevMonth.daysInMonth();
    const prev: Cell[] = Array.from({ length: firstDay }, (_, i) => {
      const day = prevDays - firstDay + 1 + i;
      return {
        day,
        type: "prev",
        year: prevMonth.year(),
        month: prevMonth.month()
      } as Cell;
    });

    // current month cells
    const curr: Cell[] = Array.from({ length: daysInMonth }, (_, i) => ({
      day: i + 1,
      type: "current",
      year,
      month
    }));

    // next month filler to fill 6 rows (42 cells)
    const total = prev.length + curr.length;
    const nextLength = 42 - total;
    const nextMonth = moment([year, month, 1]).add(1, "month");
    const next: Cell[] = Array.from({ length: nextLength }, (_, i) => ({
      day: i + 1,
      type: "next",
      year: nextMonth.year(),
      month: nextMonth.month()
    }));

    return [...prev, ...curr, ...next];
  }

  isSelectedDay(side: Side, cell: Cell) {
    const range = this.localDate[side];
    if (!range.start) return false;

    const day = this.mDay(cell);
    const start = this.toStart(range.start);
    const end = range.end ? this.toStart(range.end) : null;

    return day.isSame(start, "day") || (end && day.isSame(end, "day"));
  }

  isDisabledDay(side: Side, cell: Cell) {
    const day = this.mDay(cell);

    const main = this.localDate.main;

    // ============================
    // 1. FIRST OPEN → ALL ENABLED
    // ============================
    if (!main.start) {
      return false; // tidak ada disable
    }

    // ============================
    // 2. MAIN PANEL
    // ============================
    if (side === "main") {
      // user sudah pilih start, tapi belum pilih end
      if (main.start && !main.end) {
        const start = this.toStart(main.start);
        // allow 90 days before and 90 days after
        // actually allow 90 days before and 90 days after, but subtract 1 day for the current day
        const totalMax = 90 - 1;
        const min = start.clone().subtract(totalMax, "days");
        const max = start.clone().add(totalMax, "days");

        return day.isBefore(min, "day") || day.isAfter(max, "day");
      }

      // user sudah complete range → tidak ada restrict lagi
      return false;
    }

    // ============================
    // 3. COMPARE PANEL RULES (existing behavior)
    // ============================
    const startMain = main.start ? this.toStart(main.start) : null;
    const endMain = main.end ? this.toStart(main.end) : null;

    if (!startMain || !endMain) {
      return false; // disable NONE, tetap ikuti rule baru
    }

    // compare tidak boleh >= main.start
    const blockCompare = day.isSameOrAfter(startMain, "day");

    return blockCompare;
  }

  isInPreviewRange(side: Side, cell: Cell) {
    const norm = this.normRange(side);
    if (!norm) return false;
    const day = this.mDay(cell);
    return day.isBetween(norm.min, norm.max, undefined, "[]");
  }

  isPreviewStart(side: Side, cell: Cell) {
    const norm = this.normRange(side);
    if (!norm) return false;
    const cellMoment = this.mDay(cell);
    return cellMoment.isSame(norm.min, "day");
  }

  isPreviewEnd(side: Side, cell: Cell) {
    const norm = this.normRange(side);
    if (!norm) return false;
    const cellMoment = this.mDay(cell);
    return cellMoment.isSame(norm.max, "day");
  }

  cellClass(side: Side, cell: Cell) {
    const isCurrent = cell.type === "current";
    const selected = this.isSelectedDay(side, cell);
    const inRange = this.isInPreviewRange(side, cell);
    const disabled = this.isDisabledDay(side, cell);
    return {
      "text-black-lp-200 text-opacity-25": !isCurrent,
      "cursor-not-allowed": !isCurrent,
      "font-medium text-white bg-red-lp-100": selected && isCurrent,
      "group-hover:font-medium group-hover:text-white group-hover:bg-red-lp-100":
        isCurrent && !disabled,
      "opacity-50 cursor-not-allowed": disabled,
      "text-gray-lp-800": inRange && isCurrent && !selected
    };
  }

  handleMouseEnter(side: Side, cell: Cell) {
    if (this.isDisabledDay(side, cell) || this.selecting[side].selectingStart)
      return;
    this.hoverMoment[side] = this.mDay(cell);
  }

  handleMouseLeave(side: Side) {
    this.hoverMoment[side] = null;
  }

  onCellClick(side: Side, cell: Cell) {
    if (cell.type === "current" && !this.isDisabledDay(side, cell))
      this.selectDay(side, cell);
  }
  onCellEnter(side: Side, cell: Cell) {
    if (cell.type === "current") this.handleMouseEnter(side, cell);
  }
  onCellLeave(side: Side) {
    this.handleMouseLeave(side);
  }

  /* ---------------------- Selection ---------------------- */
  selectDay(side: Side, cell: Cell) {
    const clicked = this.mDay(cell);
    const range = this.localDate[side];

    // 1. Handle auto-range compare panel
    if (this.shouldAutoRangeCompare(side)) {
      this.setCompareAutoRange(clicked);
      this.$emit("select-date", cell);
      return;
    }

    // 2. Reset preset jika ada
    this.resetPreset();

    // 3. Start new range (klik pertama)
    if (this.isFirstClick(range)) {
      this.startNewRange(side, clicked);
      return;
    }

    // 4. Complete range (klik kedua)
    this.completeRange(side, range, clicked);

    // 5. Auto-set compare jika memilih di main panel
    if (side === "main") this.autoSetCompare(range);

    this.$emit("select-date", cell);
  }
  shouldAutoRangeCompare(side: Side) {
    const main = this.localDate.main;
    return side === "compare" && main.start && main.end;
  }
  setCompareAutoRange(clicked: moment.Moment) {
    const main = this.localDate.main;

    const totalDays = moment(main.end)
      .startOf("day")
      .diff(moment(main.start).startOf("day"), "days");

    const end = clicked.clone();
    const start = end.clone().subtract(totalDays, "days");

    const compare = this.localDate.compare;
    compare.start = this.toLocalDate(start);
    compare.end = this.toLocalDate(end);
  }
  resetPreset() {
    if (this.selectedPreset) this.selectedPreset = 0;
  }
  isFirstClick(range: Range) {
    return !range.start || range.end;
  }
  startNewRange(side: Side, clicked: moment.Moment) {
    const range = this.localDate[side];

    range.start = this.toLocalDate(clicked);
    range.end = null;

    this.selecting[side].selectingStart = false;

    if (side === "main") {
      this.localDate.compare = new Range();
      this.selecting.compare.selectingStart = true;
    }

    this.panel[side] = this.computePanelFromDate(clicked);
  }
  completeRange(side: Side, range: Range, clicked: moment.Moment) {
    const start = this.toStart(range.start);

    const isBefore = clicked.isBefore(start);

    range.start = isBefore ? this.toLocalDate(clicked) : range.start;
    range.end = isBefore ? this.toLocalDate(start) : this.toLocalDate(clicked);

    this.selecting[side].selectingStart = true;
    this.panel[side] = this.computePanelFromDate(clicked);
  }

  autoSetCompare(range: Range) {
    const mainStart = this.toStart(range.start);
    const mainEnd = this.toStart(range.end);
    const days = this.selectedPreset || mainEnd.diff(mainStart, "days");
    const compareEnd = mainStart.clone().subtract(1, "day");
    const compareStart = compareEnd.clone().subtract(days, "days");

    this.localDate.compare = new Range({
      start: this.toLocalDate(compareStart),
      end: this.toLocalDate(compareEnd)
    });
    this.panel.compare = this.computePanelFromDate(compareStart);
    this.selectedPreset = this.detectPresetFromMainDate(this.localDate);
  }

  /* ---------------------- Presets / Apply / Reset ---------------------- */
  get maxDate() {
    return this.toStart(this.max ?? new Date());
  }
  get minDate() {
    return this.maxDate.clone().subtract(90, "days");
  }

  applyPreset(days: number) {
    this.selectedPreset = days;
    this.selecting.main.selectingStart = true;
    this.selecting.compare.selectingStart = true;

    if (!days) {
      this.localDate.main = new Range();
      this.localDate.compare = new Range();
      this.panel.main = this.computePanelFromDate(moment());
      this.panel.compare = this.computePanelFromDate(
        moment().subtract(1, "month")
      );
      return;
    }

    const mainEnd = this.maxDate.clone();
    let mainStart = mainEnd.clone().subtract(days - 1, "days");
    if (mainStart.isBefore(this.minDate, "day"))
      mainStart = this.minDate.clone();

    this.localDate.main = new Range({
      start: this.toLocalDate(mainStart),
      end: this.toLocalDate(mainEnd)
    });
    const compareEnd = mainStart.clone().subtract(1, "day");
    const compareStart = compareEnd.clone().subtract(days - 1, "days");
    this.localDate.compare = new Range({
      start: this.toLocalDate(compareStart),
      end: this.toLocalDate(compareEnd)
    });

    this.panel.main = this.computePanelFromDate(mainStart);
    this.panel.compare = this.computePanelFromDate(compareStart);

    this.$emit("preset", days);
  }

  onReset() {
    this.applyPreset(this.defaultDays);
    this.$emit("reset");
  }
  get disabledApply() {
    return (
      !this.localDate.main.start ||
      !this.localDate.main.end ||
      !this.localDate.compare.start ||
      !this.localDate.compare.end
    );
  }

  apply(event?: Event) {
    this.onUpdateValue();
    this.closeOverlayPanel(event);
  }

  onUpdateValue() {
    const value = cloneDeep(
      new DateRange({
        main: this.localDate.main,
        compare: this.localDate.compare
      })
    );
    this.$emit("update:modelValue", value);
    this.$emit("apply", value);
  }

  detectPresetFromMainDate(value: DateRange): number {
    if (!value?.main?.start || !value?.main?.end) return 0;

    const start = moment(value.main.start).startOf("day");
    const end = moment(value.main.end).startOf("day");

    // diff inclusive
    const total = end.diff(start, "days") + 1;

    // must match one of the presets
    const presets = [7, 14, 30, 90];
    if (!presets.includes(total)) return 0;

    // check "end must be today"
    const today = moment().startOf("day");
    if (!end.isSame(today)) return 0;

    // success
    return total;
  }

  mounted() {
    this.applyPreset(this.defaultDays);
    if (this.autoApply) this.onUpdateValue();

    const op: any = (this.$refs as any)?.opDateRangeCompare;
    if (op) {
      op.$watch("visible", (val: boolean) => {
        if (!val)
          this.$nextTick(() => {
            this.localDate = cloneDeep(this.modelValue);
            this.selectedPreset = this.detectPresetFromMainDate(
              this.modelValue
            );
            this.syncPanelToRange();
          });
      });
    }
  }
}
